/**
 * Remembers where the user came from, so that a "back" button can return there instead of a
 * hardcoded destination.
 *
 * Pages like the patient timeline, the patient form or a medical event are reachable from several
 * places (patient list, tasks, transversal navigation, ...): any fixed destination is wrong for one
 * of them. The origin is the full URL of the previous page, filters included, so returning there
 * restores them for free.
 *
 * Stored in sessionStorage: it survives a page reload (unlike `location.state`) and stays scoped to
 * the tab.
 */

const STORAGE_KEY = 'fw_app_navigation_origins'
/** Bounded so that browsing many patients does not grow the storage indefinitely */
const MAX_ENTRIES = 30

/** Marks a navigation triggered by a back button, which must not create a new origin */
export const BACK_NAVIGATION_STATE = { isBackNavigation: true }

export const isBackNavigation = (state: unknown): boolean =>
  !!state && typeof state === 'object' && 'isBackNavigation' in state

type Origins = Record<string, string>

const readOrigins = (): Origins => {
  try {
    return (JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}') as Origins) ?? {}
  } catch {
    return {}
  }
}

const writeOrigins = (origins: Origins): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(origins))
  } catch {
    // storage full or unavailable: back buttons fall back to their default destination
  }
}

export const getNavigationOrigin = (pathname: string): string | null =>
  readOrigins()[pathname] ?? null

export const setNavigationOrigin = (pathname: string, origin: string | null): void => {
  const origins = readOrigins()
  // delete first so that re-inserting puts the key last: the oldest entries are trimmed
  delete origins[pathname]
  if (origin !== null) {
    origins[pathname] = origin
  }

  const keys = Object.keys(origins)
  keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES)).forEach((key) => delete origins[key])

  writeOrigins(origins)
}
